import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, MapPin, User, FileText, CheckSquare } from 'lucide-react';

const ProjectInfoViewer = ({ project }) => {
  return (
    <div className="space-y-6">
      {/* Project Summary */}
      <Card className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
          Project Summary
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Project Type</p>
              <p className="font-semibold text-slate-900 dark:text-white">
                {project.project_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </p>
            </div>
          </div>
          
          {project.address && (
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Location</p>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {project.address}
                  {project.city && `, ${project.city}`}
                  {project.state && `, ${project.state}`}
                </p>
              </div>
            </div>
          )}
          
          {project.client_name && (
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Client</p>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {project.client_name}
                </p>
              </div>
            </div>
          )}
          
          {project.architect_name && (
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Architect</p>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {project.architect_name}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
      
      {/* Scope of Work */}
      {project.scope_of_work && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
            <FileText className="mr-2 h-5 w-5 text-blue-600" />
            Scope of Work
          </h3>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
            {project.scope_of_work}
          </p>
        </Card>
      )}
      
      {/* Building Summary */}
      {project.building_summary && Object.keys(project.building_summary).length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Building Summary
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {project.building_summary.existing_sf && (
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Existing SF</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {project.building_summary.existing_sf.toLocaleString()}
                </p>
              </div>
            )}
            
            {project.building_summary.addition_sf && (
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Addition SF</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {project.building_summary.addition_sf.toLocaleString()}
                </p>
              </div>
            )}
            
            {project.building_summary.total_sf && (
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total SF</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {project.building_summary.total_sf.toLocaleString()}
                </p>
              </div>
            )}
            
            {project.building_summary.occupancy && (
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Occupancy</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">
                  {project.building_summary.occupancy}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* Project Team */}
      {project.consultants && project.consultants.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <User className="mr-2 h-5 w-5 text-blue-600" />
            Project Team ({project.consultants.length})
          </h3>
          
          <div className="space-y-3">
            {project.consultants.map((consultant, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    {consultant.name}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {consultant.role} {consultant.company && `• ${consultant.company}`}
                  </p>
                </div>
                {consultant.email && (
                  <a
                    href={`mailto:${consultant.email}`}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {consultant.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
      
      {/* Compliance Requirements */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <CheckSquare className="mr-2 h-5 w-5 text-blue-600" />
          Compliance Requirements
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center space-x-2">
            {project.requires_ada ? (
              <Badge className="bg-green-100 text-green-700">ADA Required</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">ADA N/A</Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {project.requires_title24 ? (
              <Badge className="bg-green-100 text-green-700">Title 24</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">Title 24 N/A</Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {project.requires_green_building ? (
              <Badge className="bg-green-100 text-green-700">CALGreen</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">CALGreen N/A</Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {project.requires_life_safety ? (
              <Badge className="bg-green-100 text-green-700">Life Safety</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">Life Safety N/A</Badge>
            )}
          </div>
        </div>
      </Card>
      
      {/* Sheet Index */}
      {project.sheets && project.sheets.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Sheet Index
          </h3>
          
          <div className="space-y-2">
            {project.sheets.filter(s => s.included).map((sheet, index) => (
              <div key={index} className="flex items-center space-x-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                <span className="font-mono text-sm font-semibold text-blue-600 min-w-[80px]">
                  {sheet.sheet_number}
                </span>
                <span className="text-slate-700 dark:text-slate-300">
                  {sheet.title}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProjectInfoViewer;